// Charge the card on file for one patient. The second step of a two-step charge: the
// row's "Charge card…" button opens this, and only the confirm button here charges.
//
// What the confirm restates, from GET …/card: the amount, the card's brand, last four
// and expiry, when and how the patient consented and the wording they agreed to, and
// what the practice's rules hold back (overdue visits, no-show fees). The amount
// defaults to what the rules allow and cannot exceed it; the server refuses a larger
// one rather than trimming it, and so does this dialog.
//
// One click, one charge:
//   - the confirm is disabled in flight and a ref guards a second click;
//   - the dialog generates one `request_id` (Stripe's idempotency key), so a second
//     request that did slip through is the same charge, not another;
//   - after any answer that took or may have taken money (201, a decline, an earlier
//     charge not posted yet, a 5xx, no answer) the confirm never re-enables;
//   - a refusal that charged nothing (the card changed, more than the rules allow,
//     a bad field) reloads what the confirm shows and takes a NEW request id, so the
//     next click is judged on what is now on screen.
//
// Posts nothing to the ledger: the payment appears when Stripe's webhook lands.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatPatientName } from "../../utils/formatName";
import {
  centsToDollars,
  chargeCard,
  chargeRefusal,
  dollarsToCents,
  fetchCard,
  formatDay,
  formatWhen,
  newRequestId,
} from "./api";
import "./Collections.css";

const VIA_LABELS = {
  pay_page: "on their statement link",
  in_person: "in person",
};

export default function ChargeCardModal({
  practiceId,
  patientId,
  patientName,
  onClose,
  onCharged,
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amountText, setAmountText] = useState("");
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState(false);
  const [message, setMessage] = useState(null);
  const inFlight = useRef(false);
  const attempted = useRef(false);
  const requestId = useRef(null);
  const closeRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await fetchCard(practiceId, patientId);
      setSummary(body);
      setAmountText(
        body.chargeableCents > 0 ? (body.chargeableCents / 100).toFixed(2) : "",
      );
      requestId.current = null;
      return body;
    } catch (err) {
      setSummary(null);
      setMessage({ type: "error", text: err.message });
      return null;
    } finally {
      setLoading(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !inFlight.current) onClose(attempted.current);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const card = summary ? summary.card : null;
  const amountCents = dollarsToCents(amountText);
  const amountValid =
    amountCents != null &&
    !Number.isNaN(amountCents) &&
    amountCents > 0 &&
    !!summary &&
    amountCents <= summary.chargeableCents;

  const refusal = !summary
    ? null
    : !summary.enabled
      ? "Card payments are not set up for this environment."
      : !card || !card.live
        ? "This patient has no card on file."
        : !card.last4
          ? "The card on file is incomplete — Atlas has no last four for it, so a charge cannot be confirmed against it."
          : chargeRefusal(summary);

  const canConfirm =
    !!summary && !refusal && amountValid && !loading && !busy && !settled;

  const confirm = async () => {
    if (!canConfirm || inFlight.current) return;
    let id;
    try {
      id = requestId.current || newRequestId();
    } catch (err) {
      setMessage({ type: "error", text: err.message });
      return;
    }
    requestId.current = id;
    inFlight.current = true;
    attempted.current = true;
    setBusy(true);
    setMessage(null);
    const charged = { amountCents, last4: card.last4 };
    try {
      const chargeableAtCharge = summary.chargeableCents;
      const result = await chargeCard(practiceId, patientId, {
        amountCents,
        consentId: card.id,
        expectedLast4: card.last4,
        requestId: id,
      });
      setSettled(true);
      onCharged(
        { ...(result || {}), chargeableCentsAtCharge: chargeableAtCharge },
        charged,
      );
    } catch (err) {
      const body = err.body || {};
      if (err.status === 409 && body.card) {
        // Nothing charged. Show the card now on file and ask again.
        const fresh = await load();
        setMessage({
          type: "warn",
          text: fresh
            ? "The card on file changed since this dialog opened. Check the card below and confirm again."
            : err.message,
        });
      } else if (err.status === 422) {
        // Nothing charged: more than the rules allow. Show what is allowed now.
        await load();
        setMessage({ type: "error", text: err.message });
      } else if ([400, 403, 404, 429, 503].includes(err.status)) {
        requestId.current = null;
        setMessage({ type: "error", text: err.message });
      } else if (err.status === 402) {
        setSettled(true);
        setMessage({
          type: "error",
          text: `${err.message}${body.decline_code ? ` (${body.decline_code})` : ""}. Nothing was charged. Contact the patient rather than retrying.`,
        });
      } else if (err.status === 409) {
        setSettled(true);
        setMessage({ type: "warn", text: err.message });
      } else {
        setSettled(true);
        setMessage({
          type: "error",
          text: "No clear answer from Stripe, so this card may or may not have been charged. Close this and check the patient's card payments before trying again.",
        });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const amountLabel = amountValid
    ? formatCurrency(centsToDollars(amountCents))
    : "—";

  return (
    <div
      className="coll-modal-backdrop"
      onClick={() => !inFlight.current && onClose(attempted.current)}
    >
      <div
        className="coll-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coll-charge-title"
      >
        <div className="coll-modal-header">
          <h2 id="coll-charge-title">Charge card on file</h2>
          <button
            ref={closeRef}
            className="coll-modal-close"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="coll-modal-body">
          {loading && !summary && <p className="coll-muted">Loading…</p>}

          {summary && (
            <>
              <p className="coll-modal-lede">
                This charges {formatPatientName(patientName)}'s card once, now.
                The payment posts to their visits when Stripe confirms it.
              </p>

              {card && (
                <dl className="coll-confirm-facts" data-testid="charge-facts">
                  <dt>Card</dt>
                  <dd>
                    {(card.brand || "Card").toUpperCase()} ····{" "}
                    {card.last4 || "—"}
                    {card.exp ? ` (exp ${card.exp})` : ""}
                  </dd>
                  <dt>Consent</dt>
                  <dd>
                    {formatWhen(card.consentedAt)}
                    {card.consentedVia && VIA_LABELS[card.consentedVia]
                      ? `, ${VIA_LABELS[card.consentedVia]}`
                      : ""}
                    {card.consentTextVersion != null
                      ? ` · wording v${card.consentTextVersion}`
                      : ""}
                  </dd>
                  <dt>Owes in total</dt>
                  <dd>
                    {formatCurrency(centsToDollars(summary.balanceCents)) ||
                      "—"}
                  </dd>
                  <dt>Rules allow</dt>
                  <dd>
                    {formatCurrency(centsToDollars(summary.chargeableCents))}
                  </dd>
                </dl>
              )}

              {card && summary.consentText && (
                <blockquote className="coll-consent-text">
                  {summary.consentText}
                </blockquote>
              )}

              {summary.held.length > 0 && (
                <div className="coll-held">
                  <strong>Not chargeable under this practice's rules</strong>
                  <ul>
                    {summary.held.map((h) => (
                      <li key={h.claimId}>
                        {formatDay(h.dos)} ·{" "}
                        {formatCurrency(centsToDollars(h.balanceCents))} —{" "}
                        {h.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {refusal ? (
                <div className="coll-message error" role="alert">
                  {refusal}
                </div>
              ) : (
                <label className="coll-field">
                  <span>Amount to charge ($)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountText}
                    disabled={busy || settled}
                    onChange={(e) => setAmountText(e.target.value)}
                  />
                  {!amountValid && amountText !== "" && (
                    <small className="coll-field-error">
                      Enter an amount above $0 and no more than{" "}
                      {formatCurrency(centsToDollars(summary.chargeableCents))}.
                    </small>
                  )}
                </label>
              )}
            </>
          )}

          {message && (
            <div className={`coll-message ${message.type}`} role="alert">
              {message.text}
            </div>
          )}
        </div>

        <div className="coll-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
          >
            {settled ? "Close" : "Cancel"}
          </button>
          <button
            className="btn btn-primary btn-sm coll-money-btn"
            onClick={confirm}
            disabled={!canConfirm}
          >
            {busy
              ? "Charging…"
              : card && amountValid
                ? `Charge ${amountLabel} to ···· ${card.last4}`
                : "Charge"}
          </button>
        </div>
      </div>
    </div>
  );
}
